import {
  buildUsercentricsSettingsIds,
  type UsercentricsSettingsIds,
} from '../utils/usercentricsConfig'

/**
 * Server-derived map of Usercentrics settings IDs, keyed by <LANG>_<COUNTRY>.
 *
 * Why useState instead of runtimeConfig.public:
 *   In Nuxt 4 `runtimeConfig.public` is deeply frozen (Object.preventExtensions)
 *   after env-var overrides are applied. A Nitro plugin can neither reassign
 *   a top-level key ("Cannot assign to read only property") nor add a nested
 *   one ("object is not extensible") — see commit history on this file. And
 *   reading process.env in static config (apps/<brand>/config/shops.ts or
 *   nuxt.config.ts) bakes `undefined` into the bundle during `pnpm build`
 *   inside Docker; see
 *   docs/solutions/build-deploy/process-env-build-time-bake-in-nuxt-static-config.md.
 *
 *   useState bridges the gap: the factory runs once during SSR (after Kamal's
 *   runtime env is loaded into the container), the resulting value is
 *   auto-serialised into the SSR payload, and the client picks it up on
 *   hydration without re-running the factory. No frozen-config conflict, no
 *   env-var renames in 1Password / deploy.yml / .env templates.
 */
export const useUsercentricsSettingsIds = () =>
  useState<UsercentricsSettingsIds>('usercentricsSettingsIds', () => {
    if (import.meta.server) {
      return buildUsercentricsSettingsIds(
        process.env as Record<string, string | undefined>,
      )
    }
    // On the client this factory is bypassed in practice — useState reads the
    // SSR-serialised value. The empty fallback is defensive only.
    return {}
  })
